import 'bootstrap/scss/bootstrap.scss';

import axios from 'axios';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { AppProviders } from './AppProviders';
import App, { IAppProps, IIdentity, isPliDomainUser } from './stories/App';
import { setUserInfo } from './utility/dataLayerUtils';
import logger from './utility/logger';

interface IDeploymentEnvironment {
    environment: string;
}

const isDevelopment = process.env.NODE_ENV === 'development';
const props: IAppProps = { identity: { id: '', name: '', email: '', individualSK: 0, isAuthorized: false,
    isPliEmployee: false, isGroupcast: false,  isIndividual: false, itemSK: 0, organizationSK: 0, isIM: false, isPM: false, hasAiAccess: false }, deploymentEnvironment: '', isDevelopment };
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const urlParamsLC = new URLSearchParams(queryString.toLowerCase());

const identityUrl = urlParams.has('t') ? ('identity/isauthorized?t=' + urlParams.get('t')) : (
    urlParamsLC.has('itemsk') ? ('identity/isauthorized?itemsk=' + urlParamsLC.get('itemsk')) : 'identity/isauthorized');

const IDENTITY_BOOTSTRAP_MAX_RETRIES = 2;
const IDENTITY_BOOTSTRAP_RETRY_DELAY_MS = 1000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * An indeterminate identity means the server could not tell whether the viewer is authorized —
 * not that they are signed out. Retrying gives a transient downstream lookup failure a chance to
 * clear before bootstrap has to render something. If every attempt still comes back indeterminate,
 * the last response is used as-is; isLoggedIn() still fails closed for first render, while
 * useAuthCheck's periodic re-check keeps an already-established session as-is on the same
 * non-answer.
 */
const fetchIdentityWithRetry = async (attemptsLeft = IDENTITY_BOOTSTRAP_MAX_RETRIES): Promise<IIdentity> => {
    const identity = await axios.get(identityUrl).then(response => response.data as IIdentity);

    if (identity.isIndeterminate && attemptsLeft > 0) {
        await delay(IDENTITY_BOOTSTRAP_RETRY_DELAY_MS);
        return fetchIdentityWithRetry(attemptsLeft - 1);
    }

    return identity;
};

const identityRequest = fetchIdentityWithRetry();

identityRequest
    .then(async (identity) => {
        let deploymentEnvironment = '';

        if (identity.isPliEmployee || isPliDomainUser(identity) || isDevelopment) {
            deploymentEnvironment = await axios.get('configuration/deployment-environment')
                .then(response => response.data as IDeploymentEnvironment)
                .then(data => data.environment)
                .catch(function () {
                    return '';
                });
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const rootElement = document.getElementById('root')!;
        const root = createRoot(rootElement);
        props.identity = identity;
        props.deploymentEnvironment = deploymentEnvironment;
        setUserInfo(props.identity);
        root.render(
            <AppProviders>
                <App {...props} />
            </AppProviders>
        );
    })
    .catch(function (error : Error) {
        // handle error
        logger.exception(error);
    })
    .finally(function () {
        // always executed
    });
